import { HiMail, HiPhone, HiLocationMarker } from 'react-icons/hi';
import { FaFilePdf, FaBraille, FaTextHeight } from 'react-icons/fa';
import { Seo } from '../../core/seo';

const Accessibility = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Accessibility"
        description="HirePanther is committed to ensuring our platform is accessible and usable by everyone, regardless of ability or technology."
        canonicalPath="/accessibility"
      />
      <div className="max-w-4xl mx-auto bg-white  p-8">
        {/* Page Title */}
        <h1 className="text-3xl font-bold text-[#284F60] mb-6 flex items-center space-x-3">
          {/* <FaUniversalAccess className="text-[#284F60] text-4xl" /> */}
          <span>Accessibility at HirePanther</span>
        </h1>

        {/* Intro Note */}
        <p className="mb-8 leading-relaxed text-[#284F60]">
          We are dedicated to ensuring our platform is accessible and usable by
          everyone, regardless of ability, technology, or situation.
        </p>

        {/* Sections */}
        <div className="space-y-8">
          {/* 1. Our Commitment */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              1. Our Commitment
            </h2>
            <p className="leading-relaxed mb-3">
              At <strong>HirePanther</strong>, we believe that everyone should
              have equal access to opportunities, services, and information. We
              are committed to making our platform, provider portal, and mobile
              apps usable by all people.
            </p>
            <p className="leading-relaxed">
              We strive to meet or exceed the{' '}
              <strong>
                Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
              </strong>
              , as well as comply with applicable accessibility legislation,
              including the <strong>Accessible Canada Act (ACA)</strong> and the{' '}
              <strong>
                Accessibility for Ontarians with Disabilities Act (AODA)
              </strong>
              .
            </p>
          </section>

          {/* 2. What We Are Doing */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              2. What We Are Doing
            </h2>
            <p className="mb-3">To deliver an inclusive experience, we have:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Designed with keyboard navigation support.</li>
              <li>
                Ensured screen reader compatibility across major browsers.
              </li>
              <li>
                Maintained high-contrast text and color usage for readability.
              </li>
              <li>
                Added alt text and descriptive labels for non-text elements.
              </li>
              <li>
                Built language switching (EN/FR) features to support Canada’s
                bilingual requirements.
              </li>
              <li>
                Tested layouts for scalability on different screen sizes and
                assistive devices.
              </li>
            </ul>
            <p className="mt-3">
              We continue to review and update our systems to keep pace with
              evolving accessibility standards.
            </p>
          </section>

          {/* 3. Continuous Improvement */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              3. Continuous Improvement
            </h2>
            <p className="mb-3">
              Accessibility is not a one-time effort. Our design, engineering,
              and content teams work together to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Conduct regular accessibility audits.</li>
              <li>
                Integrate accessibility checks into our development process.
              </li>
              <li>Provide training to our staff on accessibility awareness.</li>
              <li>Actively listen to user feedback and make adjustments.</li>
            </ul>
          </section>

          {/* 4. Known Limitations */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              4. Known Limitations
            </h2>
            <p className="mb-3">
              While we work hard to ensure an accessible experience, some areas
              may not yet be fully optimized. These may include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Certain third-party integrations or plug-ins outside of our
                control.
              </li>
              <li>Legacy content that is being updated to meet WCAG 2.1 AA.</li>
            </ul>
            <p className="mt-3">
              We are committed to fixing these issues as quickly as possible.
            </p>
          </section>

          {/* 5. Feedback & Contact */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              5. Feedback & Contact
            </h2>
            <p className="mb-4">
              We welcome feedback on how to improve accessibility. If you
              experience any barriers or have suggestions, please contact us:
            </p>
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center space-x-3">
                <HiMail className="text-[#2D617A] text-2xl" />
                <a
                  href="mailto:accessibility@hirepanther.ca"
                  className="text-[#2D617A] hover:underline text-lg"
                >
                  accessibility@hirepanther.ca
                </a>
              </div>

              {/* Phone */}
              <div className="flex items-center space-x-3">
                <HiPhone className="text-[#2D617A] text-2xl" />
                <span className="text-lg">+1 (647) 960-0666</span>
              </div>

              {/* Address */}
              <div className="flex items-center space-x-3">
                <HiLocationMarker className="text-[#2D617A] text-2xl" />
                <span className="text-lg">
                  HirePanther Accessibility, 2727 Steeles Ave W,Unit 103-1030,
                  Toronto, ON M3J 3G9, Canada
                </span>
              </div>
            </div>
            <p className="text-[#284F60] mt-3">
              We aim to respond to all inquiries within 5 business days.
            </p>
          </section>

          {/* 6. Alternative Formats */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              6. Alternative Formats
            </h2>
            <p className="mb-3">
              Upon request, we can provide information in alternative formats
              such as:
            </p>
            <ul className=" list-inside space-y-2 ml-4">
              <li className="flex items-center space-x-2">
                <FaTextHeight className="text-[#2D617A] text-lg" />
                <span>Large print</span>
              </li>
              <li className="flex items-center space-x-2">
                <FaBraille className="text-[#2D617A] text-lg" />
                <span>Braille</span>
              </li>
              <li className="flex items-center space-x-2">
                <FaFilePdf className="text-[#2D617A] text-lg" />
                <span>Accessible PDF</span>
              </li>
              <li>Plain language documents</li>
            </ul>
            <p className="mt-3">
              Requests can be made through the contact methods above.
            </p>
          </section>

          {/* 7. Legal Disclaimer */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              7. Legal Disclaimer
            </h2>
            <p className="leading-relaxed">
              This Accessibility Statement is intended to demonstrate
              HirePanther’s commitment to accessibility. While we make every
              effort to comply with applicable laws and standards, we do not
              guarantee that every aspect of our digital properties is fully
              accessible at all times. We are committed to ongoing improvements
              and addressing barriers promptly when identified.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Accessibility;
